import styled from 'styled-components';
import { Note, Source, Takeaway, UUID } from '@annote/types';
import { useRef, useState, useContext, useMemo, useCallback } from 'react';
import { colors, dimensions } from '@annote/core/styles';
import { useDragging } from 'src/context/DraggingContext';
import { ShownIdsContext } from 'src/context/ShownContext';
import { useNotes } from 'src/context/noteCacheHooks';
import { annoteIndex } from 'src/context/annoteIndex';
// import { ReactComponent as ExpandArrow } from 'src/components/Icon/icons/show_backlinks.svg';
import { ListTakeawayCard } from './byType/takeaway/ListTakeaway';
import { ListSourceCard } from './byType/source/ListSource';
import PreviewCard from './PreviewCard';

const DEFAULT_OFFSET_IN_REM = 0.7;
const CARD_OFFSET_IN_STACK_IN_REM = 0.7;
// const HOVER_OFFSET_HIDDEN_CARDS_IN_REM = 5.5;

type HiddenCardKind = 'links' | 'backlinks';

const AllButton = styled.div<{ kind: HiddenCardKind }>`
  position: absolute;
  visibility: hidden;
  ${(props) => (props.kind === 'backlinks' ? 'top: -30px; right: -30px;' : 'bottom: -30px; left: -30px;')}
  z-index: 2000;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ArrowIcon = styled.div`
  font-size: 18px;
  color: ${colors.text.button};
  line-height: 1;
`;

const HiddenWrapper = styled.div<{ kind: HiddenCardKind }>`
  display: flex;
  flex-direction: column-reverse;
  position: absolute;
  width: 100%;
  height: 100%;

  &::before {
    cursor: pointer;
    content: '';
    position: absolute;
    width: 200px;
    height: 60px;
    ${(props) => (props.kind === 'backlinks' ? 'top: -30px; right: -30px;' : 'bottom: -30px; left: -30px;')}
    z-index: 1;
  }

  /* &:hover > ${AllButton}, &::before:hover + ${AllButton} {
    visibility: visible !important;
    background: red !important;
  } */

  .card-wrapper:hover & > ${AllButton} {
    visibility: visible !important;
  }
`;

export const HiddenCardWrapper = styled.div<{ isOnBottom: boolean; offset: number }>`
  width: 100%;
  /* Use max-height to prevent cards from sticking out beyond parent */
  max-height: 90%;
  height: ${dimensions.oneInchRem * 2.9}rem;
  z-index: 10;
  display: flex;
  background: ${colors.bg.primary};
  flex-direction: column;
  justify-content: flex-start;
  position: absolute;
  bottom: ${(props) =>
    props.isOnBottom ? `${-(DEFAULT_OFFSET_IN_REM + props.offset * CARD_OFFSET_IN_STACK_IN_REM)}rem` : 'unset'};
  top: ${(props) =>
    props.isOnBottom ? 'unset' : `${-(DEFAULT_OFFSET_IN_REM + props.offset * CARD_OFFSET_IN_STACK_IN_REM)}rem`};
  left: ${(props) =>
    props.isOnBottom ? `${-(DEFAULT_OFFSET_IN_REM + props.offset * CARD_OFFSET_IN_STACK_IN_REM)}rem` : 'unset'};
  right: ${(props) =>
    props.isOnBottom ? 'unset' : `${-(DEFAULT_OFFSET_IN_REM + props.offset * CARD_OFFSET_IN_STACK_IN_REM)}rem`};

  transition: top 0.3s ease-in-out, bottom 0.3s ease-in-out;

  box-shadow: 0px 1px 2px 0px rgba(15, 15, 15, 0.16), 0px 0px 1px 0px rgba(0, 0, 0, 0.12);

  opacity: 0.6;

  &:hover {
    opacity: 1;
  }
`;

type HiddenCardProps = {
  isOnBottom?: boolean;
  offset?: number;
  note: Note;
  className?: string;
  onClick: (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => void | Promise<void>;
};

const HiddenCard: React.FC<HiddenCardProps> = ({ note, isOnBottom = false, offset = 0, onClick, className }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { isUserDragging } = useDragging();
  const ref = useRef<HTMLDivElement | null>(null);

  const handleClick: React.MouseEventHandler<HTMLDivElement> = (e) => {
    onClick(e);
  };

  // Hidden cards are not useful when a user is dragging
  if (isUserDragging) return null;

  return (
    <>
      <HiddenCardWrapper
        onClick={handleClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        isOnBottom={isOnBottom}
        offset={offset}
        className={className}
        ref={ref}
      >
        {note.type === 'takeaway' && <ListTakeawayCard takeaway={note as Takeaway} />}
        {note.type === 'source' && <ListSourceCard source={note as Source} />}
      </HiddenCardWrapper>
      {isHovered && (
        <PreviewCard
          note={note}
          isTriggerHovered={isHovered}
          triggerRef={ref}
          cornerPosition={isOnBottom ? 'bottom-left' : 'top-right'}
        />
      )}
    </>
  );
};

type HiddenCardsProps = {
  parentNoteId: UUID;
  kind: HiddenCardKind;
  relationIds: UUID[];
};

const HiddenCards: React.FC<HiddenCardsProps> = ({ parentNoteId, kind, relationIds }) => {
  // All hooks MUST come first before any early returns
  const { showAafterB, showAbeforeB, hide, shownIds } = useContext(ShownIdsContext);

  // 1. Expand skip-overs and filter by shown state
  const { hiddenIds, shownIdsFiltered } = useMemo(() => {
    const expanded = annoteIndex.expandSkipOvers(relationIds, kind);
    const hiddenNoteIds = expanded.filter((id) => !shownIds.includes(id));
    const shownNoteIds = expanded.filter((id) => shownIds.includes(id));

    return { hiddenIds: hiddenNoteIds, shownIdsFiltered: shownNoteIds };
  }, [relationIds, kind, shownIds]);

  // 2. Get notes with auto-subscribe (fetches if missing, updates on changes)
  const hiddenNotes = useNotes(hiddenIds).filter((n): n is Note => !!n);

  const showFunc = useMemo(
    () => (kind === 'backlinks' ? showAafterB : showAbeforeB),
    [kind, showAafterB, showAbeforeB],
  );
  const classPrefix = useMemo(() => (kind === 'backlinks' ? 'hidden-top-card' : 'hidden-bottom-card'), [kind]);

  const showButton: boolean = useMemo(() => {
    return hiddenNotes.length + shownIdsFiltered.length > 0;
  }, [hiddenNotes.length, shownIdsFiltered.length]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
      e.stopPropagation();
      if (hiddenNotes.length) {
        showFunc(
          hiddenNotes.map((n) => n.id),
          parentNoteId,
        );
      } else {
        hide(shownIdsFiltered);
      }
    },
    [hiddenNotes, shownIdsFiltered, parentNoteId, showFunc, hide],
  );

  // Early return AFTER all hooks to avoid violating Rules of Hooks
  if (hiddenNotes.length === 0 && shownIdsFiltered.length === 0) {
    return null;
  }

  return (
    <HiddenWrapper kind={kind}>
      {showButton && (
        <AllButton kind={kind} onClick={handleClick}>
          <ArrowIcon>
            {/* TODO - use the icon - this is a temp hack - but the svg was rendering inconsistently */}
            {hiddenNotes.length === 0 ? '↺' : '↻'}
          </ArrowIcon>
        </AllButton>
      )}
      {hiddenNotes.map((hiddenNote, i) => (
        <HiddenCard
          key={`h${kind.slice(0, 1)}-${hiddenNote.id}`}
          note={hiddenNote}
          onClick={(e) => {
            e.stopPropagation();
            showFunc(hiddenNote.id, parentNoteId);
          }}
          isOnBottom={kind === 'links'}
          offset={hiddenNotes.length - (i + 1)}
          className={`${classPrefix}-${hiddenNotes.length - i}`}
        />
      ))}
    </HiddenWrapper>
  );
};

export default HiddenCards;
